import React, { useState, useEffect } from 'react';
import { Save, Mail, Facebook, Instagram } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const ContactSettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/contact-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching contact settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/admin/contact-settings', settings);
      toast.success('Kontakteinstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving contact settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center">Lädt...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Kontakt-Einstellungen</CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Verwalten Sie Ihre Kontaktinformationen und Social Media Links
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Speichern'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* E-Mail */}
        <div>
          <Label className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Kontakt E-Mail-Adresse *
          </Label>
          <Input
            type="email"
            value={settings?.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="kontakt@ihredomain.de"
            required
            className="mt-2"
          />
          <p className="text-xs text-gray-500 mt-1">
            Diese E-Mail wird im Kontaktbereich angezeigt und erhält Nachrichten vom Kontaktformular
          </p>
        </div>

        {/* Social Media Section */}
        <div className="pt-4 border-t">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
            </svg>
            Social Media Links
          </h3>
          
          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              id="show_social_media"
              checked={settings?.show_social_media}
              onChange={(e) => handleChange('show_social_media', e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="show_social_media" className="cursor-pointer">
              Social Media Buttons anzeigen
            </Label>
          </div>

          <div className="space-y-4">
            {/* Facebook */}
            <div>
              <Label className="flex items-center gap-2">
                <div className="w-5 h-5 rounded accent-sky flex items-center justify-center">
                  <Facebook className="h-3 w-3" />
                </div>
                Facebook URL
              </Label>
              <Input
                type="url"
                value={settings?.facebook_url || ''}
                onChange={(e) => handleChange('facebook_url', e.target.value)}
                placeholder="https://facebook.com/ihreseite"
                className="mt-2"
              />
            </div>

            {/* Instagram */}
            <div>
              <Label className="flex items-center gap-2">
                <div className="w-5 h-5 rounded accent-rose flex items-center justify-center">
                  <Instagram className="h-3 w-3" />
                </div>
                Instagram URL
              </Label>
              <Input
                type="url"
                value={settings?.instagram_url || ''}
                onChange={(e) => handleChange('instagram_url', e.target.value)}
                placeholder="https://instagram.com/ihreseite"
                className="mt-2"
              />
            </div>

            {/* TikTok */}
            <div>
              <Label className="flex items-center gap-2">
                <div className="w-5 h-5 rounded accent-yellow flex items-center justify-center">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
                TikTok URL
              </Label>
              <Input
                type="url"
                value={settings?.tiktok_url || ''}
                onChange={(e) => handleChange('tiktok_url', e.target.value)}
                placeholder="https://tiktok.com/@ihreseite"
                className="mt-2"
              />
            </div>
          </div>
        </div>

        {/* Kontaktformular */}
        <div className="pt-4 border-t">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="contact_form_enabled"
              checked={settings?.contact_form_enabled}
              onChange={(e) => handleChange('contact_form_enabled', e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="contact_form_enabled" className="cursor-pointer">
              Kontaktformular aktivieren
            </Label>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Wenn aktiviert, können Besucher Ihnen Nachrichten über das Kontaktformular senden
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactSettingsManager;
